<template>
  <div class="graph">
    <svg
      width="100%"
      height="472"
      viewBox="0 0 520 472"
      fill="none"
      xmlns="http://www.w3.org/2000/svg">
      <!-- legend -->
      <rect y="460" width="16" height="8" fill="var(--color--chart--tertiary)"/>
      <text class="legend" x="25" y="468">
        {{$t('n-leaching')}} (KG N/HA)
      </text>

      <rect x="174" y="460" width="16" height="8" fill="var(--color--chart--irrigation)"/>
      <text class="legend" x="199" y="468">
        {{$t('irrigation')}} (MM)
      </text>

      <rect x="327" y="460" width="16" height="8" fill="var(--color--chart--precipitation)"/>
      <text class="legend" x="350" y="468">
        {{$t('precipitation')}} (MM)
      </text>
      <!-- /legend -->

      <!-- X axis -->
      <line x1="32" y1="408.5" x2="488" y2="408.5" stroke="#252D2B"/>
      <!-- X axis: ticks -->
      <line
        v-for="current in getChartXTicks()"
        class="tick-line"
        :key="`tick-line-${current.date}`"
        :x1="current.x"
        :x2="current.x"
        y1="409"
        y2="413"/>
      <!-- X axis: labels -->
      <text
        v-for="current in getChartXTicks()"
        class="tick-text"
        :key="`tick-text-${current.date}`"
        :x="current.x"
        y="430"
        text-anchor="start">
        {{current.date}}
      </text>
      <!-- /X axis-->

      <!-- bottom line -->
      <text
        v-for="(current, index) in getChartYLeachingTicks()"
        class="tick-text line"
        :key="`y-precipitation-and-irrigation-ticks-${index}`"
        :y="370 - (index * 40)"
        x="16"
        text-anchor="end">
        {{current}}
      </text>

      <text
        v-for="(current, index) in getChartYPrecipitationAndIrrigationTicks()"
        class="tick-text"
        :key="`y-leaching-ticks-${index}`"
        :y="370 - (index * 40)"
        x="500"
        text-anchor="start">
        {{current}}
      </text>

      <line
        v-for="(current, index) in getChartYLeachingTicks()"
        class="dash-line"
        :key="index"
        :y1="368.5 - (index * 40)"
        :y2="368.5 - (index * 40)"
        x1="32"
        x2="488"
        stroke-dasharray="2 4"/>
      <!-- /bottom line -->

      <!-- precipitation and irrigation bars -->
      <rect
        v-for="(current, index) in buffer"
        class="irrigation-bar"
        :key="`irrigation-${index}`"
        :x="getChartX(current, index, buffer)"
        :y="getChartIrrigationY(current, index, buffer)"
        :width="getChartBarWidth(current, index, buffer)"
        :height="getChartIrrigationHeight(current, index, buffer)"
      />
      <rect
        v-for="(current, index) in buffer"
        class="precipitation-bar"
        :key="`precipitation-${index}`"
        :x="getChartX(current, index, buffer)"
        :y="getChartPrecipitationY(current, index, buffer)"
        :width="getChartBarWidth(current, index, buffer)"
        :height="getChartPrecipitationHeight(current, index, buffer)"
      />
      <!-- /precipitation and irrigation bars -->

      <!-- Leaching -->
      <path
        class="leaching-graph"
        :d="getChartLeachingD()"
        stroke="var(--color--white)"
        stroke-width="8"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        class="leaching-graph"
        :d="getChartLeachingD()"
        stroke="var(--color--soil--n)"
        stroke-width="3"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <!-- /Leaching -->

      <!-- Progress line -->
      <line
        class="progress-line"
        x1="0"
        x2="0"
        y1="8"
        y2="408"
        :transform="getChartProgressTransform()" />
      <!-- /Progress line -->

      <rect
        ref="hitArea"
        x="32"
        y="0"
        width="456"
        height="408"
        fill="transparent"
        @mousemove="handleMouseMove"
        @mouseout="handleMouseOut" />
    </svg>
  </div>
</template>

<style lang="scss" scoped>

.irrigation-bar {
  fill: var(--color--chart--irrigation);
}

.precipitation-bar {
  fill: var(--color--chart--precipitation);
}

.legend {
  font-size: var(--text--xs);
  fill: var(--color--secondary);
}

.tick-text {
  font-size: var(--text--xxs);
  fill: var(--color--secondary);
  &.line {
    fill: var(--color--chart--primary);
  }
}

.progress-line {
  stroke: var(--color--primary);
}

.tick-line {
  stroke: var(--color--secondary);
}

.dash-line {
  stroke: var(--color--secondary);
}

</style>

<script>
import { mapMutations } from 'vuex'

import range from '@/helpers/range'

const CHART_Y_TICKS = 10
const CHART_X_TICKS = 5

const CHART_CONTENT_WIDTH = 456
const CHART_CONTENT_HEIGHT = 408

const CHART_LEFT_MARGIN = 32

export default {
  props: {
    progress: {
      type: Number
    },
    buffer: {
      type: Array
    },
    bufferedMin: {
      type: Object
    },
    bufferedMax: {
      type: Object
    },
    days: {
      type: Number
    }
  },
  methods: {
    ...mapMutations({
      tooltipShow: 'tooltip/show',
      tooltipHide: 'tooltip/hide'
    }),
    /* Begin of chart functions */
    getChartYTicks (count, min, max) {
      if (!isFinite(min) || !isFinite(max)) {
        return []
      }
      const ticks = []
      for (let index = 1; index <= count; index++) {
        const progress = index / count
        const tickValue = range.to(progress, min, max).toFixed(1)
        if (isFinite(tickValue)) {
          ticks.push(tickValue)
        }
      }
      return ticks
    },
    getChartYPrecipitationAndIrrigationTicks () {
      const { bufferedMin: min, bufferedMax: max } = this
      if (!min || !max) {
        return []
      }
      return this.getChartYTicks(CHART_Y_TICKS, min.PcpIrri, max.PcpIrri)
    },
    getChartYLeachingTicks () {
      const { buffer } = this

      return this.getChartYTicks(
        CHART_Y_TICKS,
        0.0, // buffer[0].NLeachedCumulated,
        buffer[buffer.length - 1].NLeachedCumulated
      )
    },

    getChartXTicks () {
      const { buffer } = this
      const totalTicks = CHART_X_TICKS
      const tickStep = Math.ceil(this.getChartEntries(buffer) / totalTicks)
      const ticks = []
      for (let index = 0; index < buffer.length; index += tickStep) {
        const row = buffer[index]
        ticks.push({
          x: this.getChartX(null, index, buffer),
          date: row.Date.replace(/\./g, '-')
        })
      }
      return ticks
    },
    getChartEntries (list) {
      return Math.max(this.days, list.length)
    },
    /* Renders a line */
    getChartD (getter) {
      // TODO: It would be great if we could have leaching directly as a value
      // from the simulation because we could remove this.
      const { buffer } = this

      if (buffer.length < 2) {
        return 'M0,0'
      }

      const valueMin = 0.0
      const valueMax = buffer[buffer.length - 1].NLeachedCumulated

      return buffer.map((current, index, list) => {
        const x = Math.round(CHART_LEFT_MARGIN + index * ((CHART_CONTENT_WIDTH / this.getChartEntries(list))))

        const value = list[index].NLeachedCumulated
        const y = Math.round(CHART_CONTENT_HEIGHT - range.from(
          value,
          valueMin,
          valueMax
        ) * CHART_CONTENT_HEIGHT)

        return index === 0
          ? `M${x},${y}`
          : `L${x},${y}`
      }).join(' ')
    },
    getChartLeachingD () {
      return this.getChartD(o => o.NLeached)
    },
    getChartLeachingY (x) {
      const { buffer } = this
      const index = Math.ceil(range.fromTo(x, 0, CHART_CONTENT_WIDTH, 0, buffer.length - 1))
      const value = buffer[index].NLeachedCumulated
      const valueMin = 0.0
      const valueMax = buffer[buffer.length - 1].NLeachedCumulated
      return CHART_CONTENT_HEIGHT - range.from(
        value,
        valueMin,
        valueMax
      ) * CHART_CONTENT_HEIGHT
    },
    getChartLeachingValue (x) {
      const { buffer } = this
      const index = Math.ceil(range.fromTo(x, 0, CHART_CONTENT_WIDTH, 0, buffer.length - 1))
      return buffer.slice(0, index + 1).reduce((acc, current) => acc + current.NLeached, 0)
    },
    getChartX (current, index, list) {
      return CHART_LEFT_MARGIN + index * this.getChartBarSpace(current, index, list)
    },
    getChartProgressX () {
      const progress = CHART_LEFT_MARGIN + this.progress * CHART_CONTENT_WIDTH
      if (!isFinite(progress)) {
        return 0
      }
      return progress
    },
    getChartProgressTransform () {
      return `translate(${this.getChartProgressX()}, ${0})`
    },
    getChartY (getter, current, index, list) {
      return CHART_CONTENT_HEIGHT - this.getChartHeight(getter, current, index, list)
    },
    getChartBarSpace (current, index, list) {
      return (CHART_CONTENT_WIDTH / this.getChartEntries(list))
    },
    getChartBarWidth (current, index, list) {
      return Math.max(1, this.getChartBarSpace(current, index, list) - 2)
    },
    getChartHeight (getter, current, index, list) {
      return range.from(
        getter(current),
        getter(this.bufferedMin),
        getter(this.bufferedMax)
      ) * CHART_CONTENT_HEIGHT
    },
    getChartPrecipitationY (current, index, list) {
      return CHART_CONTENT_HEIGHT - this.getChartPrecipitationHeight(current, index, list) - this.getChartIrrigationHeight(current, index, list)
    },
    getChartPrecipitationHeight (current, index, list) {
      return range.from(
        current.Pcp,
        this.bufferedMin.PcpIrri,
        this.bufferedMax.PcpIrri
      ) * CHART_CONTENT_HEIGHT
    },
    getChartIrrigationY (current, index, list) {
      return CHART_CONTENT_HEIGHT - this.getChartIrrigationHeight(current, index, list)
    },
    getChartIrrigationHeight (current, index, list) {
      return range.from(
        current.Irri,
        this.bufferedMin.PcpIrri,
        this.bufferedMax.PcpIrri
      ) * CHART_CONTENT_HEIGHT
    },
    /** Events */
    handleMouseMove (e) {
      const { left, top } = this.$refs.hitArea.getBoundingClientRect()
      const x = e.clientX - left
      const color = getComputedStyle(document.documentElement).getPropertyValue('--color--chart--primary')
      this.tooltipShow({
        text: this.getChartLeachingValue(x).toFixed(2),
        x: e.clientX,
        y: top + this.getChartLeachingY(x),
        color: color
      })
    },
    handleMouseOut (e) {
      this.tooltipHide()
    }
  }
}
</script>
